/**
 * DrugCatalog — drogas frecuentes en urgencias con efectos cuantitativos sobre
 * vitales. El modelo farmacodinámico es simplificado: respuesta tipo
 * rise-plateau-decay que aproxima la curva de concentración plasmática de
 * un bolo IV (no es un modelo bicompartimental, es una aproximación educativa).
 *
 * Para cada vital afectado declaramos:
 *   - delta: magnitud máxima del efecto (unidades del vital)
 *   - onsetSec: segundos hasta alcanzar el peak (aproximación)
 *   - durationSec: duración total del efecto (hasta decaer al 0)
 *
 * No modelamos interacciones fármaco-fármaco (adrenalina + beta-bloqueante, etc).
 * Es suficiente para un MVP educativo de triage/RCP.
 */

export type Vital = 'hr' | 'sbp' | 'dbp' | 'spo2' | 'rr' | 'temp';

export interface DrugEffect {
  vital: Vital;
  /** Delta máximo que alcanza. Positivo sube, negativo baja. */
  delta: number;
  /** Segundos hasta peak (ramp up lineal). */
  onsetSec: number;
  /** Segundos totales del efecto (peak + decay). */
  durationSec: number;
}

export interface DrugDef {
  id: string;
  name: string;
  /** Dosis típica para mostrar en UI. */
  dose: string;
  /** Vía de administración. */
  route: 'IV' | 'VO' | 'IM' | 'SL' | 'inhalado';
  /** Efectos esperados sobre vitales. */
  effects: DrugEffect[];
  /** Descripción breve para el log clínico. */
  note?: string;
  /** Icono para UI. */
  icon?: string;
}

/**
 * Catálogo de drogas. Los valores son aproximaciones educativas basadas en
 * ACLS/ATLS; no son recomendaciones clínicas reales para uso en pacientes.
 */
export const DRUG_CATALOG: DrugDef[] = [
  {
    id: 'adrenalina-iv',
    name: 'Adrenalina 1 mg IV',
    dose: '1 mg',
    route: 'IV',
    icon: '💉',
    note: 'Vasoconstricción + inotropismo. ACLS en RCP cada 3-5 min.',
    effects: [
      { vital: 'hr',  delta: 30, onsetSec: 30, durationSec: 180 },
      { vital: 'sbp', delta: 40, onsetSec: 45, durationSec: 240 },
      { vital: 'dbp', delta: 15, onsetSec: 45, durationSec: 240 },
    ],
  },
  {
    id: 'atropina-iv',
    name: 'Atropina 1 mg IV',
    dose: '1 mg',
    route: 'IV',
    icon: '💉',
    note: 'Vagolítico. Bradicardia sintomática.',
    effects: [
      { vital: 'hr', delta: 25, onsetSec: 60, durationSec: 300 },
    ],
  },
  {
    id: 'amiodarona-iv',
    name: 'Amiodarona 300 mg IV',
    dose: '300 mg',
    route: 'IV',
    icon: '💉',
    note: 'Antiarrítmico. FV/TV refractaria post-shock.',
    effects: [
      { vital: 'hr', delta: -15, onsetSec: 90, durationSec: 600 },
    ],
  },
  {
    id: 'morfina-iv',
    name: 'Morfina 4 mg IV',
    dose: '4 mg',
    route: 'IV',
    icon: '💉',
    note: 'Opioide. Dolor severo. Cuidado con depresión respiratoria.',
    effects: [
      { vital: 'rr',  delta: -4, onsetSec: 120, durationSec: 900 },
      { vital: 'hr',  delta: -5, onsetSec: 120, durationSec: 900 },
      { vital: 'sbp', delta: -8, onsetSec: 180, durationSec: 900 },
    ],
  },
  {
    id: 'noradrenalina-bolo',
    name: 'Noradrenalina bolo',
    dose: '10 μg',
    route: 'IV',
    icon: '💉',
    note: 'Vasopresor en shock. Idealmente BIC.',
    effects: [
      { vital: 'sbp', delta: 25, onsetSec: 20, durationSec: 120 },
      { vital: 'dbp', delta: 12, onsetSec: 20, durationSec: 120 },
    ],
  },
  {
    id: 'oxigeno-3l',
    name: 'Oxígeno 3 L/min',
    dose: '3 L/min',
    route: 'inhalado',
    icon: '🫁',
    note: 'Cánula nasal. Hipoxemia leve/moderada.',
    effects: [
      { vital: 'spo2', delta: 5, onsetSec: 30, durationSec: 3600 },
    ],
  },
  {
    id: 'oxigeno-mascarilla',
    name: 'Oxígeno mascarilla reservorio',
    dose: '15 L/min',
    route: 'inhalado',
    icon: '🫁',
    note: 'Hipoxemia severa.',
    effects: [
      { vital: 'spo2', delta: 10, onsetSec: 20, durationSec: 3600 },
    ],
  },
  {
    id: 'sf-500',
    name: 'SF 500 ml bolo',
    dose: '500 ml',
    route: 'IV',
    icon: '💧',
    note: 'Resucitación volémica. Hipotensión, shock no cardiogénico.',
    effects: [
      { vital: 'sbp', delta: 8, onsetSec: 180, durationSec: 1800 },
      { vital: 'dbp', delta: 4, onsetSec: 180, durationSec: 1800 },
    ],
  },
  {
    id: 'aspirina-vo',
    name: 'Aspirina 300 mg VO',
    dose: '300 mg',
    route: 'VO',
    icon: '💊',
    note: 'Antiagregante. SCA. No tiene efecto agudo sobre vitales.',
    effects: [],
  },
  {
    id: 'nitroglicerina-sl',
    name: 'Nitroglicerina 0.4 mg SL',
    dose: '0.4 mg',
    route: 'SL',
    icon: '💊',
    note: 'Vasodilatador. Dolor torácico no hipotenso.',
    effects: [
      { vital: 'sbp', delta: -12, onsetSec: 60, durationSec: 300 },
      { vital: 'hr',  delta: 5,   onsetSec: 60, durationSec: 300 },
    ],
  },
  {
    id: 'salbutamol-inh',
    name: 'Salbutamol 5 mg NBZ',
    dose: '5 mg',
    route: 'inhalado',
    icon: '🌬️',
    note: 'Broncodilatador. Crisis asmática / EPOC exacerbado.',
    effects: [
      { vital: 'spo2', delta: 4,  onsetSec: 90, durationSec: 1800 },
      { vital: 'hr',   delta: 10, onsetSec: 90, durationSec: 1800 },
    ],
  },
  {
    id: 'furosemida-iv',
    name: 'Furosemida 40 mg IV',
    dose: '40 mg',
    route: 'IV',
    icon: '💉',
    note: 'Diurético. EAP, insuficiencia cardíaca aguda.',
    effects: [
      { vital: 'sbp',  delta: -6, onsetSec: 300, durationSec: 3600 },
      { vital: 'spo2', delta: 3,  onsetSec: 600, durationSec: 3600 },
    ],
  },
];

export function findDrug(id: string): DrugDef | undefined {
  return DRUG_CATALOG.find(d => d.id === id);
}
